import { MusicalNoteIcon, PauseIcon, PlayIcon, VideoCameraIcon } from "@heroicons/react/24/outline";
import { PhotoIcon } from "@heroicons/react/24/solid";
import { useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { useFormContext } from "react-hook-form";
import { inputFormats } from "../_shared/input-formats";
import { Type } from "./constant";
import { isUrl, StepWrapper } from "./shared";
import { formatBytes } from "@/app/(signed-in)/create/shared/formatting-helpers";
import { trackEvent } from "@/shared/analytics-wrapper";
import { userVideoCdnBaseUrl } from "@/shared/constants";
import { acceptedFileFormat } from "@/shared/dropzone-helpers";

interface InputProps {
  step: number;
  title: string;
  file: File | undefined;
  filePath: string | undefined;
  handleUpdate: (file: File) => void;
}

export function AudioInput(props: InputProps) {
  return <InputBase {...props} type={"audio"} />;
}

export function ImageInput(props: InputProps) {
  return <InputBase {...props} type={"image"} />;
}

export function VideoInput(props: InputProps) {
  return <InputBase {...props} type={"video"} />;
}

interface BaseProps {
  type: Type;
}

function InputBase({ file, filePath, handleUpdate, title, step, type }: InputProps & BaseProps) {
  const { maxBytes } = inputFormats[type];

  const { formState } = useFormContext();
  const { getRootProps, getInputProps, fileRejections, isDragActive, open } = useDropzone({
    accept: acceptedFileFormat[type],
    maxSize: maxBytes,
    noClick: true,
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles[0]) {
        handleUpdate(acceptedFiles[0]);
      }
    },
    onDropRejected() {
      trackEvent({ eventName: "freeFaceSwapFileSizeTooLarge", action: "view", category: type });
    },
  });

  const url = useMemo(() => {
    if (filePath) {
      if (isUrl(filePath)) return filePath;
      return `${userVideoCdnBaseUrl}/${filePath}`;
    }
    if (file) {
      return URL.createObjectURL(file);
    }
  }, [file, filePath]);

  const formattedSize = formatBytes(maxBytes);

  const fileRejectionItems = fileRejections.map(({ file, errors }) => {
    return (
      <div key={file.name} className="mt-2">
        {errors.map((e) => {
          if (e.code === "file-too-large") {
            return (
              <div key={e.code} className="text-sm text-red-500">
                {`Max file size is ${formattedSize}`}
              </div>
            );
          }
        })}
      </div>
    );
  });

  const Icon = type === "image" ? PhotoIcon : type === "video" ? VideoCameraIcon : MusicalNoteIcon;

  const renderPreview = (url: string) => {
    if (type === "image") {
      return (
        <img
          className="aspect-square max-h-12 rounded-md border border-slate-700 object-cover"
          src={`${url}`}
          alt="uploaded image"
        ></img>
      );
    }

    if (type === "video") {
      return (
        <video
          className="aspect-square max-h-12 rounded-md border border-slate-700 object-cover"
          src={`${url}`}
          preload="auto"
        ></video>
      );
    }

    if (type === "audio") {
      return <AudioIcon url={url} />;
    }
  };

  return (
    <StepWrapper step={step}>
      <div className="h-full w-full overflow-hidden rounded-md">
        <div
          {...getRootProps({ className: "dropzone" })}
          className="h-full w-full flex-col items-center justify-center"
        >
          <input {...getInputProps()} disabled={formState.isSubmitting} />
          {isDragActive && (
            <div className="bg-primary/50 absolute inset-0 z-0 flex items-center justify-center backdrop-blur-md">
              <div>Drag and Drop the {type} here</div>
            </div>
          )}

          <p className="mt-4 text-center text-xs">{title}</p>

          {url ? (
            <div className="my-4 flex w-full items-center justify-center gap-2">
              {renderPreview(url)}

              <div className="btn btn-outline btn-md rounded-full capitalize" onClick={open}>
                <Icon className="h-6 w-6" />
                Change {type}
              </div>
            </div>
          ) : (
            <div className="my-4 flex w-full justify-center">
              <div className="btn btn-primary btn-md w-9/12 rounded-full capitalize" onClick={open}>
                <Icon className="h-6 w-6" />
                Upload {type}
              </div>
            </div>
          )}

          <span className="text-center text-xs">{fileRejectionItems}</span>
        </div>
      </div>
    </StepWrapper>
  );
}

function AudioIcon({ url }: { url: string }) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  const handlePlayPause = async () => {
    if (!audioRef.current) {
      return;
    }

    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(console.error);
      setIsPlaying(true);
    }
  };

  return (
    <button
      type="button"
      onClick={handlePlayPause}
      className="btn-square btn-ghost aspect-square max-h-12 rounded-md border border-slate-700 object-cover"
    >
      {isPlaying ? <PauseIcon className="m-2 size-8" /> : <PlayIcon className="m-2 size-8" />}
      <audio ref={audioRef} src={url} className="hidden" />
    </button>
  );
}
